/**
 * @module utils/dom-controller
 */
import { qs, qsa } from "utils/utils";
import isNil from "lodash/isNil";
import logger from "utils/logger";

const debugTag = "[DOM-CONTROLLER]";

/**
 * @method removeBody
 * @description removes body elements
 */
export function removeBody() {
  logger.info(`${debugTag} removeBody()`);
  const paragraphContainer = document.querySelector(".c-article-body");
  const bodyParagraph = paragraphContainer && qsa("p.c-article-body__text", paragraphContainer);

  bodyParagraph && bodyParagraph.forEach((el, i) => {
    // remove everything after the first element
    if (i) {
      el.parentElement.removeChild(el);
    }
  });
}

/**
 * @method removeElements
 * @description removes body elements
 * @param {Array} elementsToRemove
 */
export function removeElements(elementsToRemove) {
  logger.info(`${debugTag} removeElements:`, elementsToRemove);
  elementsToRemove && elementsToRemove.forEach((el) => {
    const element = qsa(el);

    element.forEach((targetElement) => {
      targetElement.innerHTML = "";
      targetElement.parentElement.removeChild(targetElement);
    });
  });
}

/**
 * @method removeAds
 * @description removes all advertisement containers so they are not tabbable
 */
export function removeAds() {
  const ads = qsa(".js-c-gpt-ad");
  if (ads.length) {
    logger.groupCollapsed(`${debugTag} removeAds() | remove ads`);
    ads.forEach(ad => {
      logger.info(`${debugTag} removed ad:`);
      logger.info(ad);
      ad.parentNode.removeChild(ad);
    });
    logger.groupEnd(`${debugTag} removeAds() | remove ads`);
  }
  // Fix accessibility issues with elements that have been added to the page via
  // third-party scripts. We're targeting the following markup (which gets added via AAM?):
  // <div style="position: absolute; margin: 0px; padding: 0px; border: 0px; height: 0px; width: 0px; overflow: hidden;">
  //   <a aria-hidden="true"></a>
  //   <button aria-hidden="true"></button>
  //   <input aria-hidden="true">
  // </div>
  const problematicButtons = qsa("body > div > button:not(.c-button)");
  if (problematicButtons.length) {
    logger.groupCollapsed(`${debugTag} removeAds() | fix problems`);
    problematicButtons.forEach(button => {
      const parentDiv = button.parentNode;
      if (!parentDiv.className) {
        const items = qsa("a, button, input", parentDiv);
        items.forEach(item => {
          if (!item.className) {
            item.setAttribute("tabindex", "-1");
            logger.info(`${debugTag} disabled keyboard navigation on element:`);
            logger.info(item);
          }
        });
      }
    });
    logger.groupEnd(`${debugTag} removeAds() | fix problems`);
  }
}

/**
 * @method removeRecommenderLinks
 * @description removes all recommenderLinks since they are lazy loaded and won't be tabbable
 */
export function removeRecommenderLinks() {
  logger.info(`${debugTag} removeRecommenderLinks()`);
  const recommenderLinks = qsa(".js-c-article-list__link");
  recommenderLinks && recommenderLinks.forEach(link => {
    link.tabIndex = "-1";
  });
}

/**
 * @method removeVideos
 * @description removes all videosLinks to not be tabbable
 */
export function removeVideos() {
  logger.info(`${debugTag} removeVideos()`);
  const videos = qsa(".powa");
  videos && videos.forEach(video => {
    video.parentNode.removeChild(video);
  });
}

/**
 * @method focusOnElement
 * @description if element exists and is visible, it will become focused
 * @param {Element|String} element
 * @returns {Boolean} true if found
 */
export function focusOnElement(element) {
  let targetElement = null;

  if (typeof element === "string") {
    targetElement = document.querySelector(element);
  } else {
    targetElement = element;
  }

  logger.info(`${debugTag} focusOnElement() | targetElement:`, targetElement);

  if (targetElement && !isNil(targetElement.offsetHeight) && targetElement.offsetHeight !== 0) {
    element.focus();
    return true;
  } else {
    return false;
  }

}

/**
 * @method filterBlacklist
 * @description if one of these stores is the current story, or the previous referrer url,
 * then remove it from the list. Also cap length minus the blacklist length.
 * @param {Array} [wraps = []] - of card feature wrapper elements.
 * @param {Array} [blacklist = []] - of undesired absolute story urls.
 * @param {String} [selector = ""] - of card anchor links selector.
 * @param {Number} [trim = 0] - amount of cards to trim off
 */
export function filterBlacklist(wraps = [], blacklist = [], selector = "", trim = 0) {
  // If you are displaying 6 articles in the UI and your blacklist has 2 articles,
  // then you should originally be querying for 8 articles (wraps) and trimming off 2 articles.
  const cap = wraps.length - trim;

  wraps.forEach((wrap, index) => {
    const link = qs(selector, wrap);
    if (link && (index >= cap || blacklist.indexOf(link.href) >= 0)) {
      wrap.parentNode.removeChild(wrap); // remove card feature wrapper element
    }
  });
}
